import { FC, useState } from "react";
import { iUser } from "../../models/User";
import { Button, TextField } from "@mui/material";
import api from "../../services/axios-client.js";

interface iPopAddEditUser {
  user: iUser;
  setUser: (user: iUser | null) => void;
  updateUsersList: (user: iUser) => void;
}

interface iUserErrors {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
}

const PopAddEditUser: FC<iPopAddEditUser> = ({
  user,
  setUser,
  updateUsersList,
}) => {
  const [u, setU] = useState<iUser>(user);
  const [errors, setErrors] = useState<iUserErrors>({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });

  const validate = () => {
    let isValid = true;
    let errors = {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
    };

    // First Name Validation
    const nameRegex = /^[a-zA-Z]{3,}$/;
    if (!u.first_name) {
      isValid = false;
      errors.first_name = "First name is required.";
    } else if (!nameRegex.test(u.first_name)) {
      isValid = false;
      errors.first_name =
        "First name must be at least 3 characters and contain only letters.";
    }

    // Last Name Validation
    if (!u.last_name) {
      isValid = false;
      errors.last_name = "Last name is required.";
    } else if (!nameRegex.test(u.last_name)) {
      isValid = false;
      errors.last_name =
        "Last name must be at least 3 characters and contain only letters.";
    }

    // Email Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!u.email) {
      isValid = false;
      errors.email = "Email is required.";
    } else if (!emailRegex.test(u.email)) {
      isValid = false;
      errors.email = "Please enter a valid email address.";
    }

    // Password Validation
    if (u.user_id === 0 || u.password) {
      const passwordRegex = /^(?=.*[0-9])(?=.*[A-Z])(?=.*[!@#$%^&*])(.{8,})$/;
      if (!u.password) {
        isValid = false;
        errors.password = "Password is required.";
      } else if (!passwordRegex.test(u.password)) {
        isValid = false;
        errors.password =
          "Password must be at least 8 characters long and include one digit, one uppercase letter, and one special character (!@#$%^&*).";
      }
    }

    setErrors(errors);
    return isValid;
  };

  const onAddUser = async () => {
    if (validate()) {
      try {
        const response = await api.post("/user", u);
        const createdUser: iUser = { ...u, user_id: response.data.user_id };
        updateUsersList(createdUser);
      } catch (error: any) {
        console.log(error);
        if (error.status === 409) {
          setErrors({ ...errors, email: error.response.data.message });
        }
      }
    }
  };

  const onEditUser = async () => {
    if (validate()) {
      try {
        const response = await api.put("/user", u);
        const updatedUser:iUser = { ...u, password:''};
        updateUsersList(updatedUser);
      } catch (error: any) {
        console.log(error);
      }
    }
  };

  return (
    <>
      <div className="pop-background"></div>

      <div className="pop-container w-[600px]">
        <div className="pop-header">
          {u.user_id ? `Edit User` : `Add New User`}
        </div>
        <div className="pop-content flex flex-col gap-4">
          <TextField
            label="First Name"
            value={u.first_name}
            onChange={(e) => setU({ ...u, first_name: e.target.value })}
            error={!!errors.first_name}
            helperText={errors.first_name}
          />
          <TextField
            label="Last Name"
            value={u.last_name}
            onChange={(e) => setU({ ...u, last_name: e.target.value })}
            error={!!errors.last_name}
            helperText={errors.last_name}
          />
          <TextField
            label="Email"
            value={u.email}
            onChange={(e) => setU({ ...u, email: e.target.value })}
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            label="Password"
            value={u.password}
            onChange={(e) => setU({ ...u, password: e.target.value })}
            error={!!errors.password}
            helperText={errors.password}
          />
        </div>
        <div className="pop-footer">
          {u.user_id === 0 && <Button onClick={onAddUser}>Add</Button>}
          {u.user_id !== 0 && <Button onClick={onEditUser}>Edit</Button>}
          <Button
            onClick={() => {
              setUser(null);
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </>
  );
};

export default PopAddEditUser;
